// <xp-rental-booking>: rental date picker for templates/product.rental.liquid.
// Renders an inline month calendar: the first tap sets the start date, the second the
// return date (tap the start again for a same-day return). Counts rental days,
// auto-selects the variant whose "rental length" value (e.g. "1 Day", "3 Days") is the
// smallest that covers those days, writes dates and day count into line item
// properties (hidden inputs joined to the product form via `form`) and shows an
// estimated total. There is no availability check.

const DAY_MS = 86400000;

function parseLengthDays(label) {
  const match = String(label || '').match(/(\d+(?:\.\d+)?)\s*(day|days|d|week|weeks|wk|wks)\b/i);
  if (!match) return null;
  const n = Number.parseFloat(match[1]);
  return /^w/i.test(match[2]) ? n * 7 : n;
}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseISODate(value) {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

class XpRentalBooking extends HTMLElement {
  connectedCallback() {
    this.startInput = this.querySelector('[data-rental-start]');
    this.returnInput = this.querySelector('[data-rental-return]');
    this.daysInput = this.querySelector('[data-rental-days-input]');
    this.unitsInput = this.querySelector('[data-rental-units-input]');
    this.form = document.getElementById(this.startInput?.getAttribute('form') || '');
    this.variants = this.#readVariants();
    this.lengths = this.variants
      .map((variant) => ({ ...variant, days: parseLengthDays(variant.label) }))
      .filter((variant) => variant.days)
      .sort((a, b) => a.days - b.days);
    this.money = new Intl.NumberFormat(this.dataset.locale || undefined, {
      style: 'currency',
      currency: this.dataset.currency || 'CAD',
    });

    const today = new Date();
    this.today = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.minDate = new Date(this.today);
    this.minDate.setDate(this.minDate.getDate() + 1);
    this.maxDate = new Date(this.minDate);
    this.maxDate.setFullYear(this.maxDate.getFullYear() + 1);
    this.start = parseISODate(this.startInput.value);
    this.end = parseISODate(this.returnInput.value);
    this.view = new Date((this.start || this.minDate).getFullYear(), (this.start || this.minDate).getMonth(), 1);
    this.focusDate = new Date(this.start || this.minDate);
    this.#setupCalendar();

    // Hidden date inputs skip native validation, so block add-to-cart until both dates are chosen.
    this.onSubmit = (event) => {
      if (event.target !== this.form) return;
      if (this.start && this.end) {
        if (this.#needsDeliveryFee() && !this.feeAdded) {
          // Add the delivery & setup line first, then resubmit the rental itself.
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#addDeliveryFee(event.submitter);
          return;
        }
        this.feeAdded = false;
        this.#prepareCharge();
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#setError(this.dataset.datesRequired || '');
      this.querySelector('[data-rental-calendar]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    document.addEventListener('submit', this.onSubmit, true);

    this.onDateChange = () => this.#update(true);
    this.onQuantityChange = (event) => {
      if (this.form && event.target instanceof Node && this.form.contains(event.target)) requestAnimationFrame(() => this.#update(false));
    };
    this.querySelectorAll("[data-rental-fulfillment]").forEach((radio) => radio.addEventListener("change", () => {
      const note = this.querySelector("[data-rental-delivery-note]");
      note?.classList.toggle("hidden", !this.querySelector("[data-rental-fulfillment=delivery]:checked"));
      this.#update(false);
    }));
    this.startInput.addEventListener('change', this.onDateChange);
    this.returnInput.addEventListener('change', this.onDateChange);
    document.addEventListener('input', this.onQuantityChange);
    document.addEventListener('change', this.onQuantityChange);
    document.addEventListener('click', this.onQuantityChange);

    // Hide the rental-length picker: the dates choose it. It stays in the DOM so the
    // theme's variant picker still swaps price, id and availability.
    if (this.lengths.length > 0) {
      document.querySelectorAll('s-variant-picker').forEach((picker) => picker.setAttribute('data-rental-hidden', ''));
    }
  }

  // Unlimited rental length: the cart line is the 1-day variant with quantity = units x days,
  // so checkout charges daily rate x units x days. The real unit count posts as a line item
  // property, and the visible quantity is restored right after the form data is read.
  #prepareCharge() {
    const days = this.#days();
    const qtyInput = this.form?.querySelector('input[name="quantity"]');
    if (!days || days < 1 || !qtyInput || !this.unitsInput) return;
    const units = this.#quantity();
    this.unitsInput.value = String(units);
    const original = qtyInput.value;
    qtyInput.value = String(units * days);
    setTimeout(() => {
      qtyInput.value = original;
    }, 0);
  }

  #needsDeliveryFee() {
    return Boolean(this.dataset.deliveryFeeVariant) && Boolean(this.querySelector('[data-rental-fulfillment=delivery]:checked'));
  }

  // Red carpet delivery & setup: a flat fee charged once per rental (quantity 1).
  async #addDeliveryFee(submitter) {
    const days = this.#days();
    if (!days || days < 1 || this.addingFee) return;
    this.addingFee = true;
    this.#setError('');
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(this.dataset.deliveryFeeVariant),
              quantity: 1,
              properties: {
                [this.dataset.propStart || 'Rental start']: this.startInput.value,
                [this.dataset.propReturn || 'Rental return']: this.returnInput.value,
                [this.dataset.propDays || 'Rental days']: String(days),
              },
            },
          ],
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      this.feeAdded = true;
      this.form.requestSubmit(submitter instanceof HTMLElement && submitter.form === this.form ? submitter : undefined);
    } catch {
      this.#setError(this.dataset.feeError || '');
    } finally {
      this.addingFee = false;
    }
  }

  disconnectedCallback() {
    document.removeEventListener('input', this.onQuantityChange);
    document.removeEventListener('change', this.onQuantityChange);
    document.removeEventListener('click', this.onQuantityChange);
    document.removeEventListener('submit', this.onSubmit, true);
  }

  #setupCalendar() {
    const locale = this.dataset.locale || undefined;
    this.monthFormat = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
    this.longFormat = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    this.tileFormat = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' });
    this.grid = this.querySelector('[data-cal-grid]');
    this.prevButton = this.querySelector('[data-cal-prev]');
    this.nextButton = this.querySelector('[data-cal-next]');
    this.selectLabel = this.querySelector('[data-rental-start-display]')?.textContent || '';

    const weekdays = this.querySelector('[data-cal-weekdays]');
    const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    weekdays.replaceChildren(
      ...Array.from({ length: 7 }, (_, i) => {
        const cell = document.createElement('span');
        cell.className = 'py-1';
        cell.textContent = weekdayFormat.format(new Date(2024, 0, 7 + i)).slice(0, 2);
        return cell;
      })
    );

    this.prevButton.addEventListener('click', () => this.#shiftMonth(-1));
    this.nextButton.addEventListener('click', () => this.#shiftMonth(1));
    this.querySelector('[data-cal-clear]')?.addEventListener('click', () => {
      this.start = null;
      this.end = null;
      this.#commitDates();
    });
    this.grid.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('button[data-date]') : null;
      if (!button || button.disabled) return;
      this.#pick(parseISODate(button.dataset.date));
    });
    this.grid.addEventListener('keydown', (event) => this.#onKeydown(event));
    this.#renderCalendar();
    this.#renderSelection();
  }

  #shiftMonth(delta) {
    const next = new Date(this.view.getFullYear(), this.view.getMonth() + delta, 1);
    if (!this.#monthInRange(next)) return;
    this.view = next;
    this.focusDate = new Date(Math.max(next.getTime(), this.minDate.getTime()));
    this.#renderCalendar();
  }

  #monthInRange(month) {
    const firstAllowed = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
    const lastAllowed = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1);
    return month >= firstAllowed && month <= lastAllowed;
  }

  #isDisabled(date) {
    if (date < this.minDate || date > this.maxDate) return true;
    return false;
  }

  #pick(date) {
    if (!date) return;
    // Package: one event date only (start = return), always charged once.
    if (this.hasAttribute('data-package')) {
      this.start = date;
      this.end = date;
      this.focusDate = new Date(date);
      this.#commitDates();
      return;
    }
    if (!this.start || this.end) {
      this.start = date;
      this.end = null;
    } else if (date < this.start) {
      this.start = date;
    } else {
      this.end = date;
    }
    this.focusDate = new Date(date);
    this.#commitDates();
  }

  #commitDates() {
    this.startInput.value = this.start ? toISODate(this.start) : '';
    this.returnInput.value = this.end ? toISODate(this.end) : '';
    this.#renderCalendar();
    this.#renderSelection();
    this.#update(true);
  }

  #renderSelection() {
    const startDisplay = this.querySelector('[data-rental-start-display]');
    const returnDisplay = this.querySelector('[data-rental-return-display]');
    startDisplay.textContent = this.start ? this.tileFormat.format(this.start) : this.selectLabel;
    if (returnDisplay) returnDisplay.textContent = this.end ? this.tileFormat.format(this.end) : this.selectLabel;
    const choosingReturn = Boolean(this.start && !this.end);
    this.querySelector('[data-rental-tile="start"]')?.classList.toggle('border-on-surface', !this.start);
    this.querySelector('[data-rental-tile="return"]')?.classList.toggle('border-on-surface', choosingReturn);
    const hint = this.querySelector('[data-cal-hint]');
    if (hint) hint.textContent = !this.start ? hint.dataset.hintStart : choosingReturn ? hint.dataset.hintReturn : hint.dataset.hintDone;
    this.querySelector('[data-cal-clear]')?.classList.toggle('hidden', !this.start);
  }

  #renderCalendar() {
    const year = this.view.getFullYear();
    const month = this.view.getMonth();
    this.querySelector('[data-cal-title]').textContent = this.monthFormat.format(this.view);
    this.prevButton.disabled = !this.#monthInRange(new Date(year, month - 1, 1));
    this.nextButton.disabled = !this.#monthInRange(new Date(year, month + 1, 1));

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const leading = new Date(year, month, 1).getDay();
    const startTime = this.start?.getTime();
    const endTime = this.end?.getTime();
    const focusISO = toISODate(this.focusDate);
    let focusable = null;

    const cells = [];
    for (let i = 0; i < leading; i++) {
      const blank = document.createElement('span');
      blank.setAttribute('role', 'gridcell');
      cells.push(blank);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const time = date.getTime();
      const iso = toISODate(date);
      const cell = document.createElement('span');
      cell.setAttribute('role', 'gridcell');
      cell.dataset.calDay = '';
      if (time === this.today.getTime()) cell.dataset.today = '';
      const isStart = startTime === time;
      const isEnd = endTime === time || (isStart && !this.end);
      if (isStart) cell.dataset.rangeStart = '';
      if (endTime === time) cell.dataset.rangeEnd = '';
      if (isStart && !this.end) cell.dataset.rangeEnd = '';
      if (startTime && endTime && time > startTime && time < endTime) cell.dataset.inRange = '';

      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.date = iso;
      button.textContent = String(day);
      button.tabIndex = -1;
      button.setAttribute('aria-label', this.longFormat.format(date));
      button.setAttribute('aria-pressed', String(isStart || isEnd));
      button.disabled = this.#isDisabled(date);
      if (iso === focusISO && !button.disabled) focusable = button;
      cell.append(button);
      cells.push(cell);
    }
    this.grid.replaceChildren(...cells);
    (focusable || this.grid.querySelector('button:not(:disabled)'))?.setAttribute('tabindex', '0');
  }

  #onKeydown(event) {
    const steps = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const button = event.target instanceof Element ? event.target.closest('button[data-date]') : null;
    if (!button) return;
    let next = null;
    const current = parseISODate(button.dataset.date);
    if (event.key in steps) {
      next = new Date(current);
      next.setDate(next.getDate() + steps[event.key]);
    } else if (event.key === 'Home' || event.key === 'End') {
      next = new Date(current);
      next.setDate(next.getDate() + (event.key === 'Home' ? -current.getDay() : 6 - current.getDay()));
    } else {
      return;
    }
    event.preventDefault();
    if (this.#isDisabled(next)) return;
    this.focusDate = next;
    if (next.getMonth() !== this.view.getMonth() || next.getFullYear() !== this.view.getFullYear()) {
      this.view = new Date(next.getFullYear(), next.getMonth(), 1);
    }
    this.#renderCalendar();
    this.grid.querySelector(`button[data-date="${toISODate(next)}"]`)?.focus();
  }

  #readVariants() {
    try {
      return JSON.parse(this.querySelector('[data-rental-variants]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  #days() {
    const start = parseISODate(this.startInput.value);
    const end = parseISODate(this.returnInput.value);
    if (!start || !end) return null;
    const diff = Math.round((end - start) / DAY_MS);
    if (diff < 0) return -1;
    return Math.max(1, diff);
  }

  #quantity() {
    const input = this.form?.querySelector('input[name="quantity"]');
    const value = Number.parseInt(input?.value || '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  #currentVariant() {
    const id = Number(this.form?.querySelector('input[name="id"]')?.value);
    return this.variants.find((variant) => variant.id === id) || this.variants[0];
  }

  #selectVariant(variant) {
    const picker = document.querySelector('s-variant-picker');
    if (!picker || !variant) return;
    const current = Number(this.form?.querySelector('input[name="id"]')?.value);
    if (current === variant.id) return;
    const radio = picker.querySelector(`input[type="radio"][data-variant-id="${variant.id}"]`);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const option = picker.querySelector(`select option[data-variant-id="${variant.id}"]`);
    if (option) {
      option.selected = true;
      option.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #setError(message) {
    const error = this.querySelector('[data-rental-error]');
    const result = this.querySelector('[data-rental-result]');
    const empty = this.querySelector('[data-rental-empty]');
    error.textContent = message;
    error.classList.toggle('hidden', !message);
    if (message) {
      result.classList.add('hidden');
      result.classList.remove('grid');
      empty.classList.add('hidden');
    }
    this.returnInput.setCustomValidity(message || '');
  }

  #update(datesChanged) {
    const days = this.#days();
    const result = this.querySelector('[data-rental-result]');
    const empty = this.querySelector('[data-rental-empty]');

    if (days === null) {
      this.daysInput.value = '';
      this.#setError('');
      result.classList.add('hidden');
      result.classList.remove('grid');
      empty.classList.remove('hidden');
      return;
    }
    if (days < 0) {
      this.daysInput.value = '';
      this.#setError(this.dataset.returnError || '');
      return;
    }

    let variant = this.#currentVariant();
    if (this.lengths.length > 0) {
      // Any length is allowed: always book the shortest (daily) variant and multiply by days.
      variant = this.lengths[0];
      if (datesChanged) this.#selectVariant(variant);
    }

    this.#setError('');
    const dayLabel = (days === 1 ? this.dataset.daysOne : this.dataset.daysOther || '').replace('[n]', String(days));
    this.daysInput.value = String(days);

    const quantity = this.#quantity();

    const daily = variant.price / (variant.days || 1);
    const isPackage = this.hasAttribute('data-package');
    this.querySelector('[data-rental-rate]').textContent = isPackage
      ? `${this.money.format(daily / 100)} × ${quantity}`
      : `${this.money.format(daily / 100)} × ${quantity} × ${dayLabel}`;
    this.querySelector('[data-rental-total]').textContent = this.money.format((daily * quantity * days) / 100);
    const deliveryLine = this.querySelector('[data-rental-delivery-fee]');
    if (deliveryLine) {
      const fee = Number(this.dataset.deliveryFeeDaily || 0);
      const showFee = fee > 0 && Boolean(this.querySelector('[data-rental-fulfillment=delivery]:checked'));
      deliveryLine.classList.toggle('hidden', !showFee);
      deliveryLine.classList.toggle('flex', showFee);
      const amount = deliveryLine.querySelector('[data-rental-delivery-amount]');
      if (amount) amount.textContent = this.money.format(fee / 100);
      // When the fee is charged at checkout, include it in the estimated total.
      if (showFee && this.dataset.deliveryFeeVariant) {
        this.querySelector('[data-rental-total]').textContent = this.money.format((daily * quantity * days + fee) / 100);
      }
    }
    empty.classList.add('hidden');
    result.classList.remove('hidden');
    result.classList.add('grid');
  }
}

if (!customElements.get('xp-rental-booking')) {
  customElements.define('xp-rental-booking', XpRentalBooking);
}
